import { type FC, useState, useEffect, useCallback, type MouseEvent } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Button } from 'dibs-elements/exports/Button.tsx';
import PlusCircle from 'dibs-icons/exports/PlusCircle.ts';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import BulkItemUploadModalSuspense from 'dibs-bulk-item-upload/exports/BulkItemUploadModalSuspense.ts';
import { type VerticalType } from 'dibs-bulk-item-upload/exports/types.ts';
import { trackNavInteraction } from '../../helpers/tracking.ts';
import { useNavContext } from '../../NavContext.tsx';
import { isMobile } from 'dibs-media-query';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './styles/NavCreateListing.scss';

import { type NavCreateListing_seller$key } from './__generated__/NavCreateListing_seller.graphql.ts';
import { type NavCreateListing_buttons$key } from './__generated__/NavCreateListing_buttons.graphql.ts';

type Props = {
    seller: NavCreateListing_seller$key;
    buttons: NavCreateListing_buttons$key;
};

const MSKU_LISTING = 'msku-listing';

const sellerFragment = graphql`
    fragment NavCreateListing_seller on Seller {
        serviceId
        vertical
        createListingOptions(userId: $userId) {
            dataTn
            text
            url
            eventName
        }
    }
`;

const buttonsFragment = graphql`
    fragment NavCreateListing_buttons on NavBarButton @relay(plural: true) {
        text
        href
        eventName
        interactionType
    }
`;

export const NavCreateListing: FC<Props> = ({ seller: sellerRef, buttons: buttonsRef }) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const buttons = useFragment(buttonsFragment, buttonsRef);
    const [isMenuVisible, setIsMenuVisible] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [isBulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
    const { status } = useNavContext();
    const isMobileView = isMobile();

    useEffect(() => {
        if (isMenuVisible) {
            trackNavInteraction({
                action: 'create listing dropdown',
                label: 'create listing',
                interactionType: 'drop down',
                isImpression: true,
            });
        }
    }, [isMenuVisible]);

    const sellerId = seller?.serviceId;
    const vertical = (seller?.vertical || null) as VerticalType;

    const { createListingOptions = [] } = seller || {};
    const multipleOptions = (createListingOptions || []).length > 1;
    const hasMskuListingOption = createListingOptions?.some(
        option => option?.dataTn === MSKU_LISTING
    );

    const { text, href, eventName, interactionType } = buttons?.[0] || {};

    const handleClick = useCallback(
        (e: MouseEvent) => {
            if (multipleOptions) {
                e.preventDefault();
                setIsMenuVisible(prev => !prev);
            }
            if (eventName) {
                trackNavInteraction({
                    action: eventName,
                    label: 'none',
                    interactionType,
                });
            }
        },
        [multipleOptions, eventName, interactionType, setIsMenuVisible]
    );

    if (isMobileView && status === 'collapsed') {
        return null;
    }

    return (
        <div
            className={classnames(
                styles.createListingButton,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.mxSmall,
                dibsCss.relative,
                {
                    [dibsCss.mbSmall]: !isMobileView,
                    [dibsCss.myXsmall]: isMobileView,
                }
            )}
        >
            {status === 'expanded' || status === 'overlay' ? (
                <Button
                    type="secondary"
                    size="medium"
                    dataTn="create-listing"
                    fullWidth
                    href={href || ''}
                    onClick={handleClick}
                    className={dibsCss.flex}
                >
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.justifyCenter,
                            dibsCss.gapXsmall
                        )}
                    >
                        <span className={styles.plusIcon}>
                            <PlusCircle variation="20" />
                        </span>
                        <span className={dibsCss.sassyFontBodyTypeHeavySizeSmall}>{text}</span>
                    </div>
                </Button>
            ) : (
                <>
                    <Link
                        href={href || ''}
                        dataTn="create-listing"
                        className={classnames(dibsCss.fillDealerprimary, dibsCss.cursorPointer)}
                        onClick={handleClick}
                        onMouseEnter={() => setTooltipOpen(true)}
                        onMouseLeave={() => setTooltipOpen(false)}
                    >
                        <PlusCircle variation="20" />
                    </Link>
                    <Tooltip
                        isVisible={tooltipOpen}
                        direction="right"
                        paddingSize="none"
                        type="autoWidth"
                        onClose={() => setTooltipOpen(false)}
                    >
                        <div
                            className={classnames(
                                dibsCss.pxSmall,
                                dibsCss.pyXsmall,
                                dibsCss.sassyFontBodyMediumHeavy,
                                dibsCss.textSatan,
                                dibsCss.textCenter,
                                dibsCss.capitalize
                            )}
                        >
                            {text}
                        </div>
                    </Tooltip>
                </>
            )}
            <Tooltip
                isVisible={isMenuVisible}
                type="autoWidth"
                direction="bottom"
                paddingSize="none"
                closeOnOutsideClick
                onClose={() => setIsMenuVisible(false)}
            >
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.pSmall,
                        dibsCss.gapXsmall,
                        styles.createListingTooltip
                    )}
                >
                    {hasMskuListingOption && (
                        <div
                            className={classnames(
                                dibsCss.sassyFontSubHeaderTypeHeavy,
                                dibsCss.mbXsmall,
                                dibsCss.whitespaceNormal
                            )}
                        >
                            <FormattedMessage
                                id="dal.navItems.createListingToooltip.question"
                                defaultMessage="What type of listing do you want to create?"
                            />
                        </div>
                    )}
                    {createListingOptions?.map(option => {
                        if (!option) {
                            return null;
                        }

                        const bulkUploadOption = option?.dataTn === 'bulk-upload';
                        const showDivider = bulkUploadOption && hasMskuListingOption;

                        return (
                            <div
                                key={option.dataTn}
                                className={classnames(dibsCss.border0, {
                                    [dibsCss.ptXsmall]: showDivider,
                                    [dibsCss.borderT]: showDivider,
                                    [dibsCss.borderSolid]: showDivider,
                                    [dibsCss.borderDolphin]: showDivider,
                                })}
                            >
                                <Link
                                    href={option.url || ''}
                                    dataTn={option.dataTn || undefined}
                                    onClick={e => {
                                        if (bulkUploadOption) {
                                            e.preventDefault();
                                            setBulkUploadModalOpen(true);
                                            setIsMenuVisible(false);
                                        }
                                        trackNavInteraction({
                                            action: option.eventName,
                                            label: option.eventName,
                                            interactionType: 'drop down',
                                        });
                                    }}
                                >
                                    <span>{option.text}</span>
                                    <ArrowRight
                                        className={classnames(
                                            styles.listingOptionIcon,
                                            dibsCss.h10px,
                                            dibsCss.ml3px
                                        )}
                                    />
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </Tooltip>
            {isBulkUploadModalOpen && (
                <BulkItemUploadModalSuspense
                    vertical={vertical}
                    sellerId={sellerId || null}
                    onClose={() => {
                        setBulkUploadModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};
